import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type { TableTemplateRecord } from "../../apiServices/tableTemplatesApi";
import { dtoToSchema } from "../../apiServices/tableTemplatesApi";
import type {
  Block,
  BlockLayout,
  TableColumn,
  TableRow,
  TableTemplate,
} from "../../models/ManualAuthoring";
import {
  canMove,
  getAllowedLayouts,
} from "../../rules/blockRuleEngine";
import { useManualAuthoringStore } from "../../store/manualAuthoringStore";
import TemplatePicker from "./TemplatePicker";

type BlockPaletteProps = {
  onSave: () => void;
  isSaving: boolean;
};

type PaletteItem = {
  layout: BlockLayout;
  label: string;
  icon: string;
};

/* ================= BLOCK DEFINITIONS ================= */

const TEXT_BLOCKS: PaletteItem[] = [
  { layout: "paragraph", label: "Paragraph", icon: "bi-type" },
  { layout: "paragraphIndented", label: "Indented", icon: "bi-text-indent-left" },
  { layout: "subheading", label: "Subheading", icon: "bi-type-h3" },
  { layout: "step", label: "Step", icon: "bi-list-ol" },
  { layout: "subStep", label: "Sub-step", icon: "bi-arrow-return-right" },
];

const LIST_BLOCKS: PaletteItem[] = [
  { layout: "bulletList", label: "Bullet List", icon: "bi-list-ul" },
  { layout: "numberList", label: "Numbered List", icon: "bi-list-ol" },
];

const CALLOUT_BLOCKS: PaletteItem[] = [
  { layout: "note", label: "Note", icon: "bi-info-circle" },
  { layout: "warning", label: "Warning", icon: "bi-exclamation-triangle" },
  { layout: "caution", label: "Caution", icon: "bi-exclamation-diamond" },
];

const FIGURE_BLOCKS: PaletteItem[] = [
  { layout: "figure", label: "Half Width", icon: "bi-columns-gap" },
];

const TABLE_BLOCKS: PaletteItem[] = [
  { layout: "table", label: "Table", icon: "bi-table" },
];

const getLeafColumns = (columns: TableColumn[]): TableColumn[] => {
  let result: TableColumn[] = [];

  for (const col of columns) {
    if (col.columns?.length) {
      result = result.concat(getLeafColumns(col.columns));
    } else {
      result.push(col);
    }
  }

  return result;
};

const createEmptyRow = (leafColumns: TableColumn[]): TableRow => ({
  cells: leafColumns.map((col) => ({
    key: col.key || col.id,
    value: [{ type: "text", value: "" }],
    align: col.align,
  })),
});

const createInitialRows = (template: TableTemplate): TableRow[] => {
  if (template.rows?.length) return template.rows;

  const leafColumns = getLeafColumns(template.columns || []);
  if (!leafColumns.length) return [];

  const rowCount = Math.max(1, template.defaultRows ?? 1);
  return Array.from({ length: rowCount }, () => createEmptyRow(leafColumns));
};

const toTableSnapshot = (record: TableTemplateRecord): TableTemplate | null => {
  const parsed = dtoToSchema(record);
  if (!parsed) return null;

  return {
    ...parsed,
    tableId: record.tableId,
    templateName: parsed.templateName || record.templateName,
    displayTitle: parsed.displayTitle || record.displayTitle,
    width: parsed.width || record.width || "100%",
    alignment: parsed.alignment || record.alignment || "left",
    headerRepeat: parsed.headerRepeat ?? record.headerRepeat ?? true,
    keepTogether: parsed.keepTogether ?? record.keepTogether ?? false,
    schemaVersion: parsed.schemaVersion || record.schemaVersion || 1,
    rows: createInitialRows(parsed),
  };
};

/* ================= COMPONENT ================= */

export default function BlockPalette({
  onSave,
  isSaving,
}: BlockPaletteProps) {
  const location = useLocation();
  const {
    presentManual,
    selectedSectionId,
    selectedSubSectionId,
    selectedBlockId,
    insertBlockAt,
    insertTableBlockAt,
    deleteBlock,
    moveBlockUp,
    moveBlockDown,
  } = useManualAuthoringStore();

  const [insertMode, setInsertMode] = useState<"below" | "end">("end");
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [tableInsertError, setTableInsertError] = useState<string | null>(null);

  /* ================= SECTION RESOLUTION ================= */

  const section = useMemo(
    () => presentManual.sections.find((s) => s.id === selectedSectionId),
    [presentManual.sections, selectedSectionId]
  );

  const subSection = useMemo(
    () =>
      selectedSubSectionId && section
        ? section.subSections?.find((s) => s.id === selectedSubSectionId)
        : null,
    [section, selectedSubSectionId]
  );

  const blocks: Block[] = useMemo(() => {
    if (!section) return [];
    if (subSection) return subSection.blocks ?? [];
    return section.blocks ?? [];
  }, [section, subSection]);

  const selectedIndex = blocks.findIndex((b) => b.id === selectedBlockId);
  const selectedBlock =
    selectedIndex >= 0 ? blocks[selectedIndex] : undefined;

  /* ================= INSERT INDEX ================= */

  const insertIndex = useMemo(() => {
    if (!blocks.length) return 0;

    if (selectedIndex >= 0 && insertMode === "below") {
      return selectedIndex + 1;
    }

    return blocks.length;
  }, [blocks, selectedIndex, insertMode]);

  /* ================= RULE FILTER ================= */

  const allowedLayouts = useMemo(() => {
    return getAllowedLayouts(blocks, insertIndex);
  }, [blocks, insertIndex]);

  /* ================= MOVE VALIDATION ================= */

  const canMoveUp =
    selectedIndex > 0 &&
    canMove(blocks, selectedIndex, selectedIndex - 1);

  const canMoveDown =
    selectedIndex >= 0 &&
    selectedIndex < blocks.length - 1 &&
    canMove(blocks, selectedIndex, selectedIndex + 1);

  /* ================= INSERT FUNCTION ================= */

  const add = (layout: BlockLayout) => {
    if (layout !== "table" && !allowedLayouts.includes(layout)) return;
    if (!section) return;

    if (layout === "table") {
      setTableInsertError(null);
      setShowTablePicker(true);
      return;
    }

    insertBlockAt(
      section.id,
      selectedSubSectionId,
      layout,
      insertIndex
    );
  };

  const handleTemplateSelect = async (templateRecord: TableTemplateRecord) => {
    if (!section) return;

    const snapshot = toTableSnapshot(templateRecord);
    if (!snapshot) {
      setTableInsertError("Selected template has invalid JSON schema.");
      return;
    }

    insertTableBlockAt(
      section.id,
      selectedSubSectionId,
      snapshot,
      insertIndex
    );
    setTableInsertError(null);
    setShowTablePicker(false);
  };

  /* ================= GROUP RENDER ================= */

  const renderGroup = (
    title: string,
    items: PaletteItem[],
    alwaysVisibleLayouts: BlockLayout[] = []
  ) => {
    const filtered = items.filter((item) =>
      alwaysVisibleLayouts.includes(item.layout) ||
      allowedLayouts.includes(item.layout)
    );

    if (filtered.length === 0) return null;

    return (
      <>
        <div className="palette-group-title">{title}</div>

        <div className="palette-grid">
          {filtered.map((item) => (
            <button
              key={item.layout}
              className="palette-tile"
              onClick={() => add(item.layout)}
              type="button"
            >
              <i className={`bi ${item.icon} palette-icon`} />
              <span className="palette-label">{item.label}</span>
            </button>
          ))}
        </div>
      </>
    );
  };

  /* ================= EARLY RETURN ================= */

  if (!section) {
    return (
      <div className="text-muted p-3">
        Select a section to add content.
      </div>
    );
  }

  const returnTo = `${location.pathname}${location.search}`;

  /* ================= RENDER ================= */

  return (
    <div className="block-palette-modern p-3">
      {selectedBlock && (
        <>
          <div className="palette-section-label">Insert Position</div>

          <div className="insert-toggle mb-3">
            <button
              className={`toggle-btn ${
                insertMode === "below" ? "active" : ""
              }`}
              onClick={() => setInsertMode("below")}
              type="button"
            >
              <i className="bi bi-arrow-down" /> Below
            </button>

            <button
              className={`toggle-btn ${
                insertMode === "end" ? "active" : ""
              }`}
              onClick={() => setInsertMode("end")}
              type="button"
            >
              <i className="bi bi-arrow-bar-down" /> End
            </button>
          </div>
        </>
      )}

      {selectedBlock && (
        <>
          <hr />
          <div className="palette-selected">
            <div className="palette-selected-layout mb-2">
              {selectedBlock.layout}
            </div>

            <div className="palette-actions">
              <button
                className="btn btn-sm btn-light"
                disabled={!canMoveUp}
                onClick={() => moveBlockUp(selectedBlock.id)}
                type="button"
              >
                ↑
              </button>

              <button
                className="btn btn-sm btn-light"
                disabled={!canMoveDown}
                onClick={() => moveBlockDown(selectedBlock.id)}
                type="button"
              >
                ↓
              </button>

              <button
                className="btn btn-sm btn-danger"
                onClick={() => deleteBlock(selectedBlock.id)}
                type="button"
              >
                🗑
              </button>
            </div>
          </div>
        </>
      )}

      {renderGroup("Text", TEXT_BLOCKS)}
      {renderGroup("Lists", LIST_BLOCKS)}
      {renderGroup("Safety", CALLOUT_BLOCKS)}
      {renderGroup("Figures", FIGURE_BLOCKS)}
      {renderGroup("Tables", TABLE_BLOCKS, ["table"])}

      <hr />

      <button
        className={`btn w-100 ${isSaving ? "btn-secondary" : "btn-primary"}`}
        onClick={onSave}
        type="button"
        disabled={isSaving}
      >
        <i className="bi bi-save me-1" /> {isSaving ? "Saving..." : "Save Content"}
      </button>

      {showTablePicker ? (
        <div className="nq-modal-backdrop" onClick={() => setShowTablePicker(false)}>
          <div className="nq-modal nq-modal--picker" onClick={(e) => e.stopPropagation()}>
            <TemplatePicker
              mode="insert"
              onClose={() => setShowTablePicker(false)}
              onTemplateSelect={(template) => void handleTemplateSelect(template)}
              returnTo={returnTo}
            />
            {tableInsertError ? (
              <div className="alert alert-danger m-3 py-2">{tableInsertError}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
